import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheckCircle } from "react-icons/fi";
import logo from "./assets/hallef-logo-cropped.png";
import "./form-validation.css";

const initialValues = { name: "", email: "", message: "" };

function validate(values) {
  const errors = {};
  if (values.name.trim().length < 2) errors.name = "Enter your name (at least 2 characters).";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = "Enter a valid email address.";
  if (values.message.trim().length < 10) errors.message = "Enter a message with at least 10 characters.";
  return errors;
}

export default function FormValidation({ errors: actualErrors, onBack } = {}) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [success, setSuccess] = useState(false);
  const formRef = useRef(null);
  const errors = validate(values);

  function handleSubmit(event) {
    event.preventDefault();
    setTouched({ name: true, email: true, message: true });
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      formRef.current.elements.namedItem(firstError).focus();
      return;
    }
    setSuccess(true);
  }

  function fieldProps(name) {
    const invalid = Boolean(touched[name] && errors[name]);
    return {
      id: `validation-${name}`, name, value: values[name], required: true,
      "aria-invalid": invalid,
      "aria-describedby": invalid ? `validation-${name}-error` : undefined,
      onBlur: () => setTouched((current) => ({ ...current, [name]: true })),
      onChange: (event) => {
        setValues((current) => ({ ...current, [name]: event.target.value }));
        setSuccess(false);
      },
    };
  }

  function fieldError(name) {
    return touched[name] && errors[name] ? (
      <span className="validation-error" id={`validation-${name}-error`}>{errors[name]}</span>
    ) : null;
  }

  if (actualErrors) {
    return (
      <main className="validation-page">
        <section className="validation-card" aria-labelledby="validation-title">
          <h1 id="validation-title">Please check your details</h1>
          <ul>{Object.entries(actualErrors).map(([field, message]) => <li key={field}>{message}</li>)}</ul>
          <button type="button" onClick={onBack}>Back to your form</button>
        </section>
      </main>
    );
  }

  return (
    <main className="validation-page">
      <Link to="/" aria-label="Hallef Technologies home"><img className="validation-logo" src={logo} alt="Hallef Technologies" /></Link>
      <section className="validation-card" aria-labelledby="validation-title">
        <p className="validation-eyebrow">LET'S CONNECT</p>
        <h1 id="validation-title">Share your details</h1>
        <p className="validation-description">Complete the fields below. All fields are required.</p>
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <label htmlFor="validation-name">Full name</label>
          <input {...fieldProps("name")} type="text" autoComplete="name" placeholder="Enter your name" />
          {fieldError("name")}
          <label htmlFor="validation-email">Email address</label>
          <input {...fieldProps("email")} type="email" autoComplete="email" placeholder="you@example.com" />
          {fieldError("email")}
          <label htmlFor="validation-message">Message</label>
          <textarea {...fieldProps("message")} rows={4} placeholder="Tell us how we can help" />
          {fieldError("message")}
          <button type="submit">Validate Details</button>
          {success && <p className="validation-success" role="status"><FiCheckCircle aria-hidden="true" /> Your details are valid. This preview does not send them.</p>}
        </form>
        <Link className="validation-home" to="/">Back to Home</Link>
      </section>
      <p className="validation-footer">Hallef Technologies</p>
    </main>
  );
}
